import { Table } from 'antd';
import { findLevelsById } from 'api/services/levels/levels.services';
import { StatusCode } from 'common/enums';
import React, { useEffect, useState } from 'react';

type Props = {
    payments: any;
}

const StudentsParentOrganismHistory: React.FC<Props> = ({ payments }) => {

    const [levelMap, setLevelMap] = useState<Record<string, string>>({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchLevels = async () => {
            const map: Record<string, string> = {};

            // Collect all unique levelIds from students
            const levelIds = Array.from(
                new Set(
                    payments
                        .map((p: any) => p.stud?.levelId)
                        .filter((id: any) => id !== undefined)
                )
            );

            for (const id of levelIds) {
                try {
                    const res = await findLevelsById(id);
                    if (res.statusCode === StatusCode.OK) {
                        map[id as string] = res.data['title'];
                    }
                } catch (err) {
                    console.error(`Failed to fetch level for id ${id}`);
                }
            }

            setLevelMap(map);
        };

        fetchLevels();
    }, [payments]);


    // Define columns for the table
    const columns = [
        {
            title: 'Élève',
            dataIndex: 'stud',
            key: 'stud',
            render: (stud: any) => {
                if (stud) {
                    return `${stud.lastname}, ${stud.firstname}`;
                } else {
                    return 'N/A';
                }
            },
            responsive: ['sm'] as any,
        },
        {
            title: 'Date du cours',
            dataIndex: 'date',
            key: 'date',
            render: (text: string) => {
                const formattedDate = text.substring(8, 10) + '-' + text.substring(5, 7) + '-' + text.substring(0, 4) + ' ' + text.substring(11, 16);
                return formattedDate;
            }
        },
        {
            title: 'Professeur',
            dataIndex: 'prof',
            key: 'prof',
            render: (prof: any) => {
                if (prof) {
                    return `${prof.lastname}, ${prof.firstname}`;
                } else {
                    return 'N/A';
                }
            },
            responsive: ['md'] as any,
        },
        {
            title: 'Matière',
            dataIndex: 'matiere',
            key: 'matiere',
            render: (text: string) => {
                return `${text}`;
            },
            responsive: ['sm'] as any,
        },
        {
            title: 'Niveau',
            dataIndex: 'stud',
            key: 'niveau',
            render: (stud: any) => {
                if (stud?.levelId && levelMap[stud.levelId]) {
                    return levelMap[stud.levelId];
                }
                return 'N/A';
            },
            responsive: ['lg'] as any,
        },
        {
            title: 'Durée',
            dataIndex: 'time',
            key: 'time',
            render: (text: string) => {
                if (text) {
                    const hours = parseInt(text.split(' ')[0]);
                    return `${hours}h`;
                } else {
                    return 'N/A';
                }
            },
            responsive: ['md'] as any,
        },
        {
            title: 'Budget utilisé',
            dataIndex: 'montant',
            key: 'montant',
            render: (amount: string) => {
                return `${amount} €`;
            }
        }
    ];

    // Mobile-specific columns - show only essential info
    const mobileColumns = [
        {
            title: 'Info',
            key: 'mobile',
            render: (record: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.95em' }}>
                        {record.stud ? `${record.stud.lastname}, ${record.stud.firstname}` : 'N/A'}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                        {record.date.substring(8, 10)}-{record.date.substring(5, 7)}-{record.date.substring(0, 4)}
                    </div>
                    <div style={{ fontSize: '0.85em' }}>
                        {record.matiere}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                        Prof: {record.prof ? `${record.prof.lastname}, ${record.prof.firstname}` : 'N/A'}
                    </div>
                </div>
            ),
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            align: 'right' as const,
            render: (amount: string) => (
                <div style={{ fontWeight: 'bold', fontSize: '0.95em' }}>
                    {amount} €
                </div>
            ),
        }
    ];

    return (
        <div style={{
            overflowX: 'auto',
            width: '100%'
        }}>
            <Table
                dataSource={payments}
                columns={isMobile ? mobileColumns : columns}
                pagination={{
                    pageSize: isMobile ? 5 : 10,
                    showSizeChanger: !isMobile,
                    responsive: true,
                }}
                scroll={{ x: isMobile ? undefined : 'max-content' }}
                size={isMobile ? 'small' : 'middle'}
            />
        </div>
    );
};

export default StudentsParentOrganismHistory;